import React, { useEffect, useState } from 'react';
import { createProduct, updateProduct } from '../services/productService';
import CatalogManagerModal from './CatalogManagerModal';

const EMPTY_FORM = {
  name: '',
  productTypeId: '',
  productUseId: '',
  departmentId: '',
  packageType: '',
  packageQuantity: '',
  cost: '',
  price: '',
};

function CatalogLabel({ children, onManage, title }) {
  return (
    <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {children}
      <button
        type="button"
        onClick={onManage}
        title={title}
        style={{
          background: 'var(--orig-green-dim)',
          border: '1px solid rgba(5,150,105,0.35)',
          borderRadius: 6,
          cursor: 'pointer',
          width: 22, height: 22,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--orig-green-bright)', fontSize: 12, lineHeight: 1,
          textTransform: 'none', letterSpacing: 0,
          transition: 'background 0.15s, transform 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(5,150,105,0.3)'; e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--orig-green-dim)'; e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <i className="bi bi-gear-fill" />
      </button>
    </label>
  );
}

export default function ProductForm({ product, departments, productTypes, productUses, onCancel, onSaved, onCatalogsChange }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [catalogModal, setCatalogModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const isEditing = Boolean(product);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        productTypeId: product.productTypeId ?? '',
        productUseId: product.productUseId ?? '',
        departmentId: product.departmentId ?? '',
        packageType: product.packageType || '',
        packageQuantity: product.packageQuantity || '',
        cost: product.cost ?? '',
        price: product.price ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError('');
  }, [product]);

  function handleChange(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    const payload = {
      name: form.name.trim(),
      packageType: form.packageType.trim(),
      packageQuantity: form.packageQuantity.trim(),
      cost: Number(form.cost) || 0,
      price: Number(form.price) || 0,
      departmentId: Number(form.departmentId),
      productTypeId: Number(form.productTypeId),
      productUseId: Number(form.productUseId),
    };

    try {
      if (isEditing) {
        await updateProduct(product.id, payload);
      } else {
        await createProduct(payload);
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.msg || 'No se pudo guardar el producto.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="section-card">
      <div className="section-header">
        <h6>
          <i className={`bi ${isEditing ? 'bi-pencil-square' : 'bi-plus-circle'}`} style={{ color: 'var(--orig-green)' }} />
          {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
        </h6>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="section-body">
          {error && (
            <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
              <i className="bi bi-exclamation-triangle-fill" />
              {error}
            </div>
          )}

          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">Nombre del producto</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: Salsa Passatas"
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                required
              />
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label">Tipo de empaque</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: FRASCO"
                value={form.packageType}
                onChange={e => handleChange('packageType', e.target.value)}
                required
              />
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label">Cantidad de empaque</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: 500ML"
                value={form.packageQuantity}
                onChange={e => handleChange('packageQuantity', e.target.value)}
              />
            </div>

            <div className="col-12 col-md-4">
              <CatalogLabel onManage={() => setCatalogModal('productType')} title="Administrar tipos de producto">
                Tipo de producto
              </CatalogLabel>
              <select
                className="form-select"
                value={form.productTypeId}
                onChange={e => handleChange('productTypeId', e.target.value)}
                required
              >
                <option value="" disabled>Seleccione...</option>
                {productTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4">
              <CatalogLabel onManage={() => setCatalogModal('productUse')} title="Administrar usos de producto">
                Uso del producto
              </CatalogLabel>
              <select
                className="form-select"
                value={form.productUseId}
                onChange={e => handleChange('productUseId', e.target.value)}
                required
              >
                <option value="" disabled>Seleccione...</option>
                {productUses.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4">
              <CatalogLabel onManage={() => setCatalogModal('department')} title="Administrar departamentos">
                Departamento
              </CatalogLabel>
              <select
                className="form-select"
                value={form.departmentId}
                onChange={e => handleChange('departmentId', e.target.value)}
                required
              >
                <option value="" disabled>Seleccione...</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label">Costo</label>
              <input
                type="number"
                className="form-control"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={form.cost}
                onChange={e => handleChange('cost', e.target.value)}
              />
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label">Precio</label>
              <input
                type="number"
                className="form-control"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={form.price}
                onChange={e => handleChange('price', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
        }}>
          <button type="button" className="btn-orig btn-orig-outline" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
            <i className="bi bi-check-lg" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>

      {catalogModal && (
        <CatalogManagerModal
          type={catalogModal}
          items={
            catalogModal === 'department' ? departments :
              catalogModal === 'productType' ? productTypes :
                productUses
          }
          onClose={() => setCatalogModal(null)}
          onChange={onCatalogsChange}
        />
      )}
    </div>
  );
}
